/* eslint-disable indent */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Spin,
  Input,
  Row,
  Col,
  Form,
  message,
  Button,
  Radio,
  Select,
  Drawer,
} from "antd";
import ExcelJS from "exceljs";
import Smcs_Loader from "../../../assests/images/loader-new.gif";
import NormalAPI from "../../../api";
import urls from "../../../api/urls";
import { getUserAndPremiseId } from "../../../utils";
import XRayAuditResultTable from "./XRayAuditResultTable";
import "./style.scss";

const { TextArea } = Input;

const dgClassOptions = Array.from({ length: 9 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1),
}));

const DEFAULT_GLOBAL_HUB = { hubId: null, hubName: "" };

const getGlobalHub = () => {
  const storedHub = localStorage.getItem("active_hub");
  if (!storedHub) {
    return DEFAULT_GLOBAL_HUB;
  }
  try {
    const activeHub = JSON.parse(storedHub);
    if (activeHub?.hubId) {
      return {
        hubId: activeHub.hubId,
        hubName: activeHub.hubName || activeHub.premiseName || "",
      };
    }
  } catch (error) {
    console.error("X-Ray global hub parse error:", error);
  }
  return DEFAULT_GLOBAL_HUB;
};

const emptyForm = {
  awbNo: "",
  auditedHubId: undefined,
  auditedHubName: "",
  auditedByEmpId: "",
  auditedByEmpName: "",
  dgType: "NDG",
  dgClass: undefined,
  remarks: "",
};

const XRayAudit = ({ activeKey, tabKey }) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const [globalHub, setGlobalHub] = useState(getGlobalHub);

  // Bulk-upload drawer state.
  const bulkInputRef = useRef(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResults, setBulkResults] = useState([]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const applyGlobalHub = useCallback((hub) => {
    setGlobalHub(hub);
    setForm((prev) => ({
      ...prev,
      auditedHubId: hub.hubId || undefined,
      auditedHubName: hub.hubName || "",
    }));
  }, []);

  useEffect(() => {
    if (activeKey === tabKey) {
      const hub = getGlobalHub();
      setGlobalHub(hub);
      setSubmitResult(null);
      setForm({
        ...emptyForm,
        auditedHubId: hub.hubId || undefined,
        auditedHubName: hub.hubName || "",
      });
    }
  }, [activeKey, tabKey]);

  useEffect(() => {
    const onHubChanged = () => applyGlobalHub(getGlobalHub());
    window.addEventListener("hubChanged", onHubChanged);
    return () => window.removeEventListener("hubChanged", onHubChanged);
  }, [applyGlobalHub]);

  const handleSubmit = async () => {
    const awbNo = form.awbNo?.trim();

    if (!awbNo) {
      message.warning("Please enter an AWB or bag number");
      return;
    }
    if (!globalHub.hubId) {
      message.warning("Please select a hub from the header");
      return;
    }
    if (!form.auditedByEmpId?.trim()) {
      message.warning("Please enter the audited by employee id");
      return;
    }
    if (!form.auditedByEmpName?.trim()) {
      message.warning("Please enter the audited by employee name");
      return;
    }
    if (form.dgType === "DG" && !form.dgClass) {
      message.warning("Please select a DG class (1-9)");
      return;
    }

    const userInfo = getUserAndPremiseId();
    const headers = {
      "SMCS-PREMISE-ID": userInfo?.premiseId,
      "USER-ID": userInfo?.userId,
    };

    const payload = {
      awbNo,
      auditedHubId: globalHub.hubId,
      auditedHubName: globalHub.hubName || null,
      auditedByEmpId: form.auditedByEmpId.trim(),
      auditedByEmpName: form.auditedByEmpName.trim(),
      dgType: form.dgType,
      dgClass: form.dgType === "DG" ? form.dgClass : null,
      remarks: form.remarks?.trim() || null,
    };

    try {
      setLoading(true);
      const response = await NormalAPI.Api(
        urls.XRAY_AUDIT_CREATE
      ).postCustomHeader(payload, headers);
      const data = response?.response?.data;
      if (response.status) {
        setSubmitResult(
          data?.totalAwbs > 1 || data?.failureCount > 0 ? data : null
        );
        if (data?.failureCount > 0) {
          message.warning(
            `${data.successCount} audited, ${data.failureCount} failed`
          );
        } else {
          message.success(data?.message || "X-Ray audit created");
        }
        setForm((prev) => ({
          ...emptyForm,
          auditedHubId: prev.auditedHubId,
          auditedHubName: prev.auditedHubName,
        }));
      } else {
        setSubmitResult(null);
        message.error(data?.message || "Failed to create X-Ray audit");
      }
    } catch (error) {
      console.error("X-Ray audit create error:", error);
      message.error("Something went wrong while creating the X-Ray audit");
    } finally {
      setLoading(false);
    }
  };

  const openBulkDrawer = () => {
    setBulkFile(null);
    setBulkFileName("");
    setBulkResults([]);
    setBulkOpen(true);
  };

  // Build the sample template in the browser so there is no static asset to
  // ship/emit. Column order here must match the backend bulk parser.
  const downloadTemplate = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const ws = workbook.addWorksheet("X-Ray Audit");
      ws.columns = [
        { header: "AWB / Bag Number", key: "awb", width: 20 },
        { header: "Audited By Emp Id*", key: "empId", width: 18 },
        { header: "Audited By Emp Name*", key: "empName", width: 22 },
        { header: "Type (DG/NDG)", key: "type", width: 15 },
        { header: "DG Class (1-9)", key: "dgClass", width: 15 },
        { header: "Remarks", key: "remarks", width: 30 },
      ];
      ws.getRow(1).font = { bold: true };
      // Force every column to text so AWBs/ids are read verbatim by the backend.
      ws.columns.forEach((_, i) => (ws.getColumn(i + 1).numFmt = "@"));
      ws.addRow({
        awb: "1234567890",
        empId: "EMP001",
        empName: "Ramesh Kumar",
        type: "DG",
        dgClass: "3",
        remarks: "Flammable liquid detected",
      });
      ws.addRow({
        awb: "9876543210",
        empId: "EMP002",
        empName: "Suresh Patel",
        type: "NDG",
        dgClass: "",
        remarks: "Clear - no dangerous goods",
      });
      ws.addRow({
        awb: "26201619418",
        empId: "EMP003",
        empName: "Anita Desai",
        type: "NDG",
        dgClass: "",
        remarks: "Bag number - audits every shipment inside the bag",
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "SampleXRayAudit.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("X-Ray template download error:", error);
      message.error("Could not generate the sample template");
    }
  };

  const onBulkFileChange = (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      message.warning("Only .xlsx / .xls files are allowed");
      return;
    }
    if (file.size > 5000000) {
      message.warning("Maximum upload size is 5 MB");
      return;
    }
    setBulkFile(file);
    setBulkFileName(file.name);
    setBulkResults([]);
  };

  const removeBulkFile = () => {
    if (bulkInputRef.current) bulkInputRef.current.value = "";
    setBulkFile(null);
    setBulkFileName("");
  };

  const handleBulkUpload = async () => {
    if (!globalHub.hubId) {
      message.warning("Please select a hub from the header");
      return;
    }
    if (!bulkFile) {
      message.warning("Please choose a file to upload");
      return;
    }
    const formData = new FormData();
    formData.append("file", bulkFile);
    formData.append("hubId", globalHub.hubId);
    try {
      setBulkLoading(true);
      const response = await NormalAPI.Api(
        urls.XRAY_AUDIT_BULK
      ).postIdHeaderFormData(formData);
      if (response.status) {
        const data = response?.response?.data;
        setBulkResults(data?.failures || []);
        if (data?.failureCount > 0) {
          message.warning(
            `${data.successCount} succeeded, ${data.failureCount} failed`
          );
        } else {
          message.success(data?.message || "Bulk X-Ray audit successful");
        }
        removeBulkFile();
      } else {
        message.error(
          response?.response?.data?.message || "Bulk X-Ray audit failed"
        );
      }
    } catch (error) {
      console.error("Bulk X-Ray audit error:", error);
      message.error("Something went wrong during bulk upload");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <Spin
      spinning={loading}
      indicator={<img src={Smcs_Loader} style={{ height: "100px", width: "100px" }} />}
    >
      <div className="master-main-container">
        <div style={{ width: "95%", padding: "10px 10px 10px 3%" }} className="global-form">
          <Row justify="end" style={{ marginBottom: "10px" }}>
            <Col>
              <Button
                type="default"
                style={{ height: "40px", borderRadius: "5px", fontWeight: "bold" }}
                onClick={openBulkDrawer}
              >
                Bulk Upload
              </Button>
            </Col>
          </Row>
          <Form layout="vertical">
            <Row gutter={20} align="top">
              <Col span={6}>
                <Form.Item label="AWB / Bag Number" required>
                  <Input
                    className="indepentedInput"
                    placeholder="Enter AWB or bag number"
                    style={{ height: "45px" }}
                    value={form.awbNo}
                    onChange={(e) =>
                      setField("awbNo", e.target.value.replace(/[^a-zA-Z0-9]/g, ""))
                    }
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Audited Hub Name" required>
                  <Select
                    className="indepentedInput"
                    style={{ width: "100%", height: "45px" }}
                    placeholder="Select a hub from the header"
                    options={
                      globalHub.hubId
                        ? [{ value: globalHub.hubId, label: globalHub.hubName }]
                        : []
                    }
                    value={globalHub.hubId || undefined}
                    disabled
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Audited By Emp Id" required>
                  <Input
                    className="indepentedInput"
                    placeholder="Enter employee id"
                    style={{ height: "45px" }}
                    value={form.auditedByEmpId}
                    onChange={(e) => setField("auditedByEmpId", e.target.value)}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Audited By Emp Name" required>
                  <Input
                    className="indepentedInput"
                    placeholder="Enter employee name"
                    style={{ height: "45px" }}
                    value={form.auditedByEmpName}
                    onChange={(e) => setField("auditedByEmpName", e.target.value)}
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={20} align="top">
              <Col span={6}>
                <Form.Item label="Type" required>
                  <Radio.Group
                    value={form.dgType}
                    onChange={(e) => {
                      const value = e.target.value;
                      setForm((prev) => ({
                        ...prev,
                        dgType: value,
                        dgClass: value === "DG" ? prev.dgClass : undefined,
                      }));
                    }}
                  >
                    <Radio value="DG">DG</Radio>
                    <Radio value="NDG">Non DG</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="DG Class (1-9)" required={form.dgType === "DG"}>
                  <Select
                    style={{ width: "100%", height: "45px" }}
                    placeholder="Select DG class"
                    options={dgClassOptions}
                    value={form.dgClass}
                    disabled={form.dgType !== "DG"}
                    onChange={(value) => setField("dgClass", value)}
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="Content / Remarks">
                  <TextArea
                    placeholder="Enter remarks"
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    value={form.remarks}
                    onChange={(e) => setField("remarks", e.target.value)}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end">
              <Col>
                <Button
                  type="primary"
                  className="global-button"
                  style={{
                    width: "150px",
                    height: "45px",
                    borderRadius: "5px",
                    fontWeight: "bold",
                  }}
                  loading={loading}
                  onClick={handleSubmit}
                >
                  Submit Audit
                </Button>
              </Col>
            </Row>
          </Form>

          {submitResult && (
            <XRayAuditResultTable
              summary={{
                successCount: submitResult.successCount,
                failureCount: submitResult.failureCount,
              }}
              failures={submitResult.failures || []}
            />
          )}
        </div>
      </div>

      <Drawer
        title="Bulk X-Ray Audit Upload"
        placement="right"
        width={560}
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        maskClosable={false}
      >
        <Spin spinning={bulkLoading}>
          <p style={{ marginBottom: "8px" }}>
            Upload a spreadsheet where each row is one X-Ray audit. A row may
            hold an AWB or a bag number; a bag audits every shipment inside it.
            Columns must match the sample template. Supported:{" "}
            <b>.xlsx / .xls</b>, max 5 MB.
          </p>
          <p style={{ marginBottom: "16px" }}>
            <a onClick={downloadTemplate} style={{ cursor: "pointer" }}>
              Download sample template for import
            </a>
          </p>

          <Form layout="vertical">
            <Form.Item label="Audited Hub Name" required>
              <Select
                style={{ width: "100%", height: "45px" }}
                placeholder="Select a hub from the header"
                options={
                  globalHub.hubId
                    ? [{ value: globalHub.hubId, label: globalHub.hubName }]
                    : []
                }
                value={globalHub.hubId || undefined}
                disabled
              />
            </Form.Item>
          </Form>

          <Button type="primary" onClick={() => bulkInputRef.current?.click()}>
            Choose File
          </Button>
          <input
            type="file"
            hidden
            ref={bulkInputRef}
            accept=".xlsx,.xls"
            onChange={onBulkFileChange}
          />
          {bulkFileName ? (
            <div style={{ marginTop: "12px" }}>
              <span style={{ marginRight: "8px" }}>{bulkFileName}</span>
              <Button size="small" onClick={removeBulkFile}>
                X
              </Button>
            </div>
          ) : null}

          <div style={{ marginTop: "20px" }}>
            <Button style={{ marginRight: "10px" }} onClick={() => setBulkOpen(false)}>
              Cancel
            </Button>
            <Button
              type="primary"
              disabled={!bulkFile || !globalHub.hubId}
              loading={bulkLoading}
              onClick={handleBulkUpload}
            >
              Upload
            </Button>
          </div>

          {bulkResults.length > 0 && (
            <XRayAuditResultTable failures={bulkResults} />
          )}
        </Spin>
      </Drawer>
    </Spin>
  );
};

export default XRayAudit;
